import React from 'react';

/**
 * TextToHyperlink - A reusable class that converts text to hyperlinks with custom listeners
 */
class TextToHyperlink {
  constructor() {
    this.listeners = new Map();
  }

  /**
   * Register a listener for a specific text pattern
   * @param {string} pattern - The text pattern to match (regex string or plain text)
   * @param {Function} callback - Function to call when the pattern is clicked
   * @param {Object} options - Additional options
   */
  addListener(pattern, callback, options = {}) {
    const {
      caseSensitive = false,
      wholeWord = false,
      className = 'text-hyperlink',
      style = { color: '#1976d2', cursor: 'pointer', textDecoration: 'underline' }
    } = options;

    const listener = {
      pattern,
      callback,
      caseSensitive,
      wholeWord,
      className,
      style
    };

    this.listeners.set(pattern, listener);
    return this; // Allow chaining
  }

  /**
   * Remove a listener for a specific pattern
   * @param {string} pattern - The text pattern to remove
   */
  removeListener(pattern) {
    this.listeners.delete(pattern);
    return this;
  }

  /**
   * Clear all listeners
   */
  clearListeners() {
    this.listeners.clear();
    return this;
  }

  /**
   * Convert text with registered patterns to React elements
   * @param {string} text - The input text to process
   * @returns {Array} Array of React elements (text and links)
   */
  convertToHyperlinks(text) {
    if (!text || typeof text !== 'string') {
      return [text];
    }

    const elements = [];
    let lastIndex = 0;
    const sortedListeners = Array.from(this.listeners.entries()).sort((a, b) => b[0].length - a[0].length);

    const processText = (remainingText, startIndex = 0) => {
      if (!remainingText) return [];

      let earliestMatch = null;
      let earliestIndex = Infinity;

      // Find the earliest match among all listeners
      for (const [pattern, listener] of sortedListeners) {
        const regex = this.createRegex(pattern, listener.caseSensitive, listener.wholeWord);
        const match = regex.exec(remainingText);
        
        if (match && match.index < earliestIndex) {
          earliestMatch = { match, listener, pattern };
          earliestIndex = match.index;
        }
      }

      if (!earliestMatch) {
        // No more matches, return remaining text
        return [remainingText];
      }

      const { match, listener, pattern } = earliestMatch;
      const beforeText = remainingText.substring(0, match.index);
      const matchedText = match[0];
      const afterText = remainingText.substring(match.index + matchedText.length);

      const result = [];
      
      // Add text before the match
      if (beforeText) {
        result.push(beforeText);
      }

      // Add the hyperlink element
      const hyperlink = (
        <span
          key={`${startIndex}-${match.index}`}
          className={listener.className}
          style={listener.style}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            listener.callback(matchedText, match, e);
          }}
          onMouseEnter={(e) => {
            if (listener.style['&:hover']) {
              Object.assign(e.target.style, listener.style['&:hover']);
            }
          }}
          onMouseLeave={(e) => {
            if (listener.style['&:hover']) {
              const hoverStyle = listener.style['&:hover'];
              Object.keys(hoverStyle).forEach(key => {
                e.target.style[key] = '';
              });
              // Re-apply base styles (excluding &:hover)
              const baseStyle = { ...listener.style };
              delete baseStyle['&:hover'];
              Object.assign(e.target.style, baseStyle);
            }
          }}
          role="link"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              listener.callback(matchedText, match, e);
            }
          }}
        >
          {matchedText}
        </span>
      );

      result.push(hyperlink);

      // Recursively process the remaining text
      if (afterText) {
        const remainingElements = processText(afterText, startIndex + match.index + matchedText.length);
        result.push(...remainingElements);
      }

      return result;
    };

    return processText(text);
  }

  /**
   * Create a regex pattern based on options
   * @param {string} pattern - The text pattern
   * @param {boolean} caseSensitive - Whether the match should be case sensitive
   * @param {boolean} wholeWord - Whether to match whole words only
   * @returns {RegExp} The created regex
   */
  createRegex(pattern, caseSensitive, wholeWord) {
    let regexPattern = pattern;
    
    // Escape special regex characters if pattern is not already a regex
    if (!pattern.startsWith('/') && !pattern.endsWith('/')) {
      regexPattern = pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    } else {
      // Remove the surrounding slashes if it's a regex pattern
      regexPattern = pattern.slice(1, -1);
    }

    if (wholeWord) {
      regexPattern = `\\b${regexPattern}\\b`;
    }

    const flags = caseSensitive ? 'g' : 'gi';
    return new RegExp(regexPattern, flags);
  }

  /**
   * Get all registered listeners
   * @returns {Map} All listeners
   */
  getListeners() {
    return this.listeners;
  }
}

/**
 * React component that uses TextToHyperlink class
 */
export const HyperlinkText = ({ text, hyperlinkInstance, fallbackProps = {}, ...props }) => {
  if (!hyperlinkInstance || !(hyperlinkInstance instanceof TextToHyperlink)) {
    // Return fallback with clickable span if instance is not available
    return (
      <span 
        {...props} 
        {...fallbackProps}
        style={{ 
          cursor: 'pointer', 
          color: 'inherit',
          textDecoration: 'none',
          ...fallbackProps.style,
          ...props.style
        }}
        onClick={fallbackProps.onClick}
      >
        {text}
      </span>
    );
  }

  try {
    const elements = hyperlinkInstance.convertToHyperlinks(text);
    
    return (
      <span {...props}>
        {elements.map((element, index) => 
          React.isValidElement(element) 
            ? React.cloneElement(element, { key: index })
            : <span key={index}>{element}</span>
        )}
      </span>
    );
  } catch (error) {
    // Fallback to plain text with click handler
    return (
      <span 
        {...props} 
        {...fallbackProps}
        style={{ 
          cursor: 'pointer', 
          color: 'inherit',
          textDecoration: 'none',
          ...fallbackProps.style,
          ...props.style
        }}
        onClick={fallbackProps.onClick}
      >
        {text}
      </span>
    );
  }
};

/**
 * Hook for creating and managing TextToHyperlink instances
 */
export const useTextToHyperlink = () => {
  const hyperlinkInstance = React.useRef(null);
  const [isReady, setIsReady] = React.useState(false);
  const listenersRef = React.useRef(new Map());

  // Initialize instance
  React.useEffect(() => {
    // Create new instance
    const newInstance = new TextToHyperlink();
    hyperlinkInstance.current = newInstance;
    
    // Re-attach any queued listeners
    listenersRef.current.forEach((config, pattern) => {
      newInstance.addListener(pattern, config.callback, config.options);
    });
    
    setIsReady(true);
    
    return () => {
      if (hyperlinkInstance.current) {
        hyperlinkInstance.current.clearListeners();
      }
    };
  }, []);

  const addListener = React.useCallback((pattern, callback, options) => {
    const config = { callback, options };
    
    // Store the listener config
    listenersRef.current.set(pattern, config);
    
    // If instance is ready, add listener immediately
    if (hyperlinkInstance.current && isReady) {
      return hyperlinkInstance.current.addListener(pattern, callback, options);
    }
    return null;
  }, [isReady]);

  const removeListener = React.useCallback((pattern) => {
    // Remove from queued listeners
    listenersRef.current.delete(pattern);
    
    // Remove from instance if ready
    if (hyperlinkInstance.current && isReady) {
      hyperlinkInstance.current.removeListener(pattern);
    }
  }, [isReady]);

  const clearListeners = React.useCallback(() => {
    // Clear queued listeners
    listenersRef.current.clear();
    
    // Clear instance listeners if ready
    if (hyperlinkInstance.current && isReady) {
      hyperlinkInstance.current.clearListeners();
    }
  }, [isReady]);

  const convertText = React.useCallback((text) => {
    if (hyperlinkInstance.current && isReady) {
      return hyperlinkInstance.current.convertToHyperlinks(text);
    }
    return [text];
  }, [isReady]);

  // Force re-initialization method
  const reinitialize = React.useCallback(() => {
    if (hyperlinkInstance.current) {
      hyperlinkInstance.current.clearListeners();
    }
    const newInstance = new TextToHyperlink();
    hyperlinkInstance.current = newInstance;
    
    // Re-attach all queued listeners
    listenersRef.current.forEach((config, pattern) => {
      newInstance.addListener(pattern, config.callback, config.options);
    });
  }, []);

  return {
    instance: hyperlinkInstance.current,
    addListener,
    removeListener,
    clearListeners,
    convertText,
    reinitialize,
    isReady
  };
};

export default TextToHyperlink;
